import { useEffect } from "react";
import { useLocation } from "react-router";

/**
 * On pathname change: scroll to top.
 * On hash links (e.g. `/#categories`): scroll to that element after the page mounts.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.replace(/^#/, ""));
      if (!id) return undefined;

      const scrollToHash = () => {
        const el = document.getElementById(id);
        if (!el) return false;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      };

      // Element may not exist on the first paint (route just mounted).
      if (scrollToHash()) return undefined;

      const raf = requestAnimationFrame(() => {
        if (scrollToHash()) return;
        // Retry shortly for heavier home sections.
        setTimeout(scrollToHash, 120);
        setTimeout(scrollToHash, 400);
      });

      return () => cancelAnimationFrame(raf);
    }

    const scrollNow = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollNow();
    const raf = requestAnimationFrame(scrollNow);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}
